import { useEffect, useMemo, useRef, useState } from "react";
import type { GeoJSONSource, MapGeoJSONFeature, Map as MapLibreMap, MapMouseEvent, Popup } from "maplibre-gl";
import type { Project } from "../types";
import { isNewPermit } from "../permit-labels";
import { completionEstimateExplanation } from "../completion-estimate";

type MappableProject = Project & { latitude: number; longitude: number };
type ProjectProperties = { id: string; address: string; neighborhood: string; zipCode: string; builderName: string | null; beds: number | null; baths: number | null; hasPool: boolean; isNew: boolean; projectType: Project["projectType"]; predictedCompletionMonth: string | null };

const AUSTIN_CENTER: [number, number] = [-97.7431, 30.2672];
const SOURCE_ID = "permit-projects";
const CLUSTERS_LAYER = "permit-clusters";
const CLUSTER_COUNT_LAYER = "permit-cluster-count";
const PROJECTS_LAYER = "permit-projects-unclustered";
const PROJECT_ICONS_LAYER = "permit-project-icons";
const SINGLE_FAMILY_ICON = "permit-single-family-icon";
const MULTI_UNIT_ICON = "permit-multi-unit-icon";
const OPENFREEMAP_STYLE = "https://tiles.openfreemap.org/styles/positron";

function formatPredictionMonth(value: string) {
  const date = new Date(`${value}-15T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(date);
}

function hasCoordinates(project: Project): project is MappableProject {
  return Number.isFinite(project.latitude) && Number.isFinite(project.longitude);
}

function toFeatureCollection(projects: MappableProject[]) {
  return { type: "FeatureCollection" as const, features: projects.map((project) => ({
    type: "Feature" as const,
    geometry: { type: "Point" as const, coordinates: [project.longitude, project.latitude] },
    properties: {
      id: project.id,
      address: project.address,
      neighborhood: project.neighborhood ?? "Neighborhood pending",
      zipCode: project.zipCode ?? "",
      builderName: project.builderName,
      beds: project.beds ?? null,
      baths: project.baths ?? null,
      hasPool: project.hasPool,
      isNew: isNewPermit(project.issuedDate ?? ""),
      projectType: project.projectType,
      predictedCompletionMonth: project.predictedCompletionMonth ?? null,
    } satisfies ProjectProperties,
  })) };
}

function addMarkerIcon(map: MapLibreMap, name: string, draw: (context: CanvasRenderingContext2D) => void) {
  const canvas = document.createElement("canvas");
  canvas.width = 48;
  canvas.height = 48;
  const context = canvas.getContext("2d");
  if (!context) return;
  context.strokeStyle = "#ffffff";
  context.lineWidth = 4;
  context.lineCap = "round";
  context.lineJoin = "round";
  draw(context);
  map.addImage(name, context.getImageData(0, 0, canvas.width, canvas.height));
}

function addMarkerIcons(map: MapLibreMap) {
  addMarkerIcon(map, SINGLE_FAMILY_ICON, (context) => {
    context.beginPath();
    context.moveTo(9, 24);
    context.lineTo(24, 11);
    context.lineTo(39, 24);
    context.lineTo(39, 38);
    context.lineTo(9, 38);
    context.closePath();
    context.moveTo(20, 38);
    context.lineTo(20, 29);
    context.lineTo(28, 29);
    context.lineTo(28, 38);
    context.stroke();
  });
  addMarkerIcon(map, MULTI_UNIT_ICON, (context) => {
    context.beginPath();
    context.moveTo(8, 18);
    context.lineTo(19, 9);
    context.lineTo(30, 18);
    context.lineTo(30, 29);
    context.lineTo(8, 29);
    context.closePath();
    context.moveTo(18, 29);
    context.lineTo(18, 23);
    context.moveTo(18, 29);
    context.lineTo(40, 29);
    context.lineTo(40, 18);
    context.lineTo(29, 9);
    context.stroke();
  });
}

function text(tag: string, value: string, className?: string) {
  const element = document.createElement(tag);
  element.textContent = value;
  if (className) element.className = className;
  return element;
}

function completionEstimateInfo() {
  const info = text("span", "i", "completion-estimate-info completion-estimate-info-map");
  info.tabIndex = 0;
  info.setAttribute("aria-label", "About this estimated completion date");
  info.append(text("span", completionEstimateExplanation, "completion-explainer"));
  return info;
}

function makeProjectLinkInteractive(element: HTMLAnchorElement, projectId: string, onOpenProject?: (id: string) => void) {
  for (const eventName of ["pointerdown", "touchstart"] as const) {
    element.addEventListener(eventName, (event) => event.stopPropagation());
  }
  element.addEventListener("touchend", (event) => {
    event.stopPropagation();
    if (!onOpenProject) return;
    event.preventDefault();
    onOpenProject(projectId);
  }, { passive: false });
  element.addEventListener("click", (event) => {
    event.stopPropagation();
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!onOpenProject) return;
    event.preventDefault();
    onOpenProject(projectId);
  });
}

function projectPreview(properties: ProjectProperties, onOpenProject?: (id: string) => void) {
  const section = document.createElement("section");
  section.className = "marker-project-preview";
  const featureLabels = [
    properties.beds ? `${properties.beds} beds` : null,
    properties.baths ? `${properties.baths} baths` : null,
    properties.hasPool ? "Pool permit" : null,
  ].filter((label): label is string => Boolean(label));
  const location = document.createElement("div");
  location.className = "marker-preview-location";
  const address = document.createElement("a");
  address.className = "marker-popup-address";
  address.href = `/projects/${encodeURIComponent(properties.id)}`;
  address.setAttribute("aria-label", `View permit details for ${properties.address}`);
  address.append(
    text("span", properties.address, "marker-popup-address-text"),
    text("span", "View permit details →", "marker-popup-action"),
  );
  makeProjectLinkInteractive(address, properties.id, onOpenProject);
  location.append(address, text("p", [properties.neighborhood, properties.zipCode, ...featureLabels].filter(Boolean).join(" · ")));
  section.append(location);
  if (properties.builderName) section.append(text("p", `Builder · ${properties.builderName}`, "marker-popup-builder"));
  if (properties.predictedCompletionMonth) {
    const completion = text("p", `Est. completion ${formatPredictionMonth(properties.predictedCompletionMonth)}`, "marker-popup-completion");
    completion.append(completionEstimateInfo());
    section.append(completion);
  }
  return section;
}

function projectPicker(features: MapGeoJSONFeature[], onSelect: (id: string) => void, onOpenProject?: (id: string) => void) {
  const section = document.createElement("section");
  section.className = "cluster-picker-popup";
  section.append(text("strong", `${features.length} listings at this location`), text("p", "Select a listing to view its map details or open the full record."));
  const list = document.createElement("ul");
  for (const feature of features) {
    const properties = feature.properties as ProjectProperties;
    const item = document.createElement("li");
    const button = text("button", properties.address) as HTMLButtonElement;
    button.type = "button";
    button.addEventListener("click", () => onSelect(properties.id));
    const link = text("a", "Open record") as HTMLAnchorElement;
    link.href = `/projects/${encodeURIComponent(properties.id)}`;
    makeProjectLinkInteractive(link, properties.id, onOpenProject);
    item.append(button, link);
    list.append(item);
  }
  section.append(list);
  return section;
}

function addProjectLayers(map: MapLibreMap, highlightedId: string) {
  map.addLayer({ id: CLUSTERS_LAYER, type: "circle", source: SOURCE_ID, filter: ["has", "point_count"], paint: {
    "circle-color": "#d94b0b", "circle-radius": ["step", ["get", "point_count"], 18, 20, 21, 75, 25, 150, 29], "circle-stroke-color": "#ffffff", "circle-stroke-width": 3, "circle-opacity": 0.96,
  } });
  map.addLayer({ id: CLUSTER_COUNT_LAYER, type: "symbol", source: SOURCE_ID, filter: ["has", "point_count"], layout: {
    "text-field": ["get", "point_count_abbreviated"], "text-font": ["Noto Sans Regular"], "text-size": 13,
  }, paint: { "text-color": "#ffffff" } });
  map.addLayer({ id: PROJECTS_LAYER, type: "circle", source: SOURCE_ID, filter: ["!", ["has", "point_count"]], paint: {
    "circle-color": ["case", ["==", ["get", "isNew"], true], "#2f855a", "#d94b0b"], "circle-radius": ["case", ["==", ["get", "id"], highlightedId], 19, 14], "circle-stroke-color": "#ffffff", "circle-stroke-width": 3, "circle-opacity": 0.96,
  } });
  map.addLayer({ id: PROJECT_ICONS_LAYER, type: "symbol", source: SOURCE_ID, filter: ["!", ["has", "point_count"]], layout: {
    "icon-image": ["case", ["==", ["get", "projectType"], "multi_unit"], MULTI_UNIT_ICON, SINGLE_FAMILY_ICON], "icon-size": 0.43, "icon-allow-overlap": true, "icon-ignore-placement": true,
  } });
}

export function PermitMap({ projects, selectedId, highlightedId, onSelect, onOpenProject }: { projects: Project[]; selectedId: string; highlightedId: string; onSelect: (id: string) => void; onOpenProject?: (id: string) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const selectedIdRef = useRef(selectedId);
  const onSelectRef = useRef(onSelect);
  const onOpenProjectRef = useRef(onOpenProject);
  const projectsRef = useRef<MappableProject[]>([]);
  const persistentPopupRef = useRef<Popup | null>(null);
  const hoverPopupRef = useRef<Popup | null>(null);
  const [mapStatus, setMapStatus] = useState<"loading" | "ready" | "error">("loading");
  const [mapAttempt, setMapAttempt] = useState(0);
  const mappableProjects = useMemo(() => projects.filter(hasCoordinates), [projects]);
  const hasMappableProjects = mappableProjects.length > 0;

  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  useEffect(() => { onOpenProjectRef.current = onOpenProject; }, [onOpenProject]);
  useEffect(() => { projectsRef.current = mappableProjects; }, [mappableProjects]);

  useEffect(() => {
    if (!containerRef.current || !mappableProjects.length) return;
    let disposed = false;
    let observer: ResizeObserver | null = null;
    let map: MapLibreMap | null = null;
    let readinessTimer: number | null = null;
    let hoverCloseTimer: number | null = null;
    let initializing = false;
    setMapStatus("loading");

    const initialize = async () => {
      if (initializing || !containerRef.current || containerRef.current.clientWidth === 0 || containerRef.current.clientHeight === 0) return;
      initializing = true;
      try {
        const maplibregl = await import("maplibre-gl");
        if (disposed || !containerRef.current) return;
        maplibregl.setWorkerUrl("/maplibre-gl-worker.mjs");
        const initiallySelected = mappableProjects.find((project) => project.id === selectedIdRef.current) ?? mappableProjects[0];
        const initialCenter: [number, number] = initiallySelected ? [initiallySelected.longitude, initiallySelected.latitude] : AUSTIN_CENTER;
        const currentMap = new maplibregl.Map({ container: containerRef.current, style: OPENFREEMAP_STYLE, center: initialCenter, zoom: projectsRef.current.length === 1 ? 16 : 11.5, minZoom: 9, maxZoom: 19, attributionControl: {} });
        map = currentMap;
        mapRef.current = currentMap;
        currentMap.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
        readinessTimer = window.setTimeout(() => {
          if (!disposed) setMapStatus("error");
        }, 15000);

        const clearHoverPopup = () => {
          if (hoverCloseTimer !== null) window.clearTimeout(hoverCloseTimer);
          hoverCloseTimer = null;
          hoverPopupRef.current?.remove();
          hoverPopupRef.current = null;
        };
        const scheduleHoverPopupClose = () => {
          if (hoverCloseTimer !== null) window.clearTimeout(hoverCloseTimer);
          hoverCloseTimer = window.setTimeout(clearHoverPopup, 180);
        };
        const showProjectPopup = (feature: MapGeoJSONFeature, persistent: boolean) => {
          const properties = feature.properties as ProjectProperties;
          const coordinates = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
          const content = projectPreview(properties, onOpenProjectRef.current);
          if (!persistent) {
            content.addEventListener("mouseenter", () => {
              if (hoverCloseTimer !== null) window.clearTimeout(hoverCloseTimer);
              hoverCloseTimer = null;
            });
            content.addEventListener("mouseleave", scheduleHoverPopupClose);
          }
          const popup = new maplibregl.Popup({ closeButton: persistent, closeOnClick: false, offset: 24, className: persistent ? "project-marker-popup" : "project-marker-tooltip" })
            .setLngLat(coordinates).setDOMContent(content).addTo(currentMap);
          if (persistent) {
            persistentPopupRef.current?.remove();
            persistentPopupRef.current = popup;
            onSelectRef.current(properties.id);
          } else {
            hoverPopupRef.current?.remove();
            hoverPopupRef.current = popup;
          }
        };

        const onClusterClick = async (event: MapMouseEvent) => {
          const feature = currentMap.queryRenderedFeatures(event.point, { layers: [CLUSTERS_LAYER] })[0];
          if (!feature) return;
          const source = currentMap.getSource(SOURCE_ID) as GeoJSONSource;
          const zoom = await source.getClusterExpansionZoom(Number(feature.properties?.cluster_id));
          currentMap.easeTo({ center: (feature.geometry as GeoJSON.Point).coordinates as [number, number], zoom, duration: 320 });
        };
        const onProjectClick = (event: MapMouseEvent) => {
          const features: MapGeoJSONFeature[] = currentMap.queryRenderedFeatures(event.point, { layers: [PROJECTS_LAYER, PROJECT_ICONS_LAYER] });
          if (!features.length) return;
          const unique = Array.from(new Map<string, MapGeoJSONFeature>(features.map((feature) => [String(feature.properties?.id), feature])).values());
          if (unique.length === 1) showProjectPopup(unique[0], true);
          else {
            persistentPopupRef.current?.remove();
            persistentPopupRef.current = new maplibregl.Popup({ closeButton: true, closeOnClick: false, offset: 24, className: "cluster-picker-maplibre-popup" })
              .setLngLat(event.lngLat).setDOMContent(projectPicker(unique, onSelectRef.current, onOpenProjectRef.current)).addTo(currentMap);
          }
        };
        const onProjectEnter = (event: MapMouseEvent) => {
          currentMap.getCanvas().style.cursor = "pointer";
          const feature = currentMap.queryRenderedFeatures(event.point, { layers: [PROJECTS_LAYER, PROJECT_ICONS_LAYER] })[0];
          if (hoverCloseTimer !== null) window.clearTimeout(hoverCloseTimer);
          hoverCloseTimer = null;
          if (feature) showProjectPopup(feature, false);
        };
        const onProjectLeave = () => { currentMap.getCanvas().style.setProperty("cursor", ""); scheduleHoverPopupClose(); };
        const onClusterEnter = () => { currentMap.getCanvas().style.setProperty("cursor", "pointer"); };
        const onClusterLeave = () => { currentMap.getCanvas().style.setProperty("cursor", ""); };

        currentMap.on("error", (event) => {
          console.error("MapLibre render error", event.error);
          if (!currentMap.isStyleLoaded()) setMapStatus("error");
        });
        currentMap.on("style.load", () => {
          currentMap.addSource(SOURCE_ID, {
            type: "geojson",
            data: toFeatureCollection(projectsRef.current),
            cluster: true,
            clusterMaxZoom: 16,
            clusterRadius: 46,
          });
          addMarkerIcons(currentMap);
          addProjectLayers(currentMap, highlightedId);
          currentMap.on("click", CLUSTERS_LAYER, onClusterClick);
          currentMap.on("click", PROJECTS_LAYER, onProjectClick);
          if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
            currentMap.on("mouseenter", PROJECTS_LAYER, onProjectEnter);
            currentMap.on("mouseleave", PROJECTS_LAYER, onProjectLeave);
          }
          currentMap.on("mouseenter", CLUSTERS_LAYER, onClusterEnter);
          currentMap.on("mouseleave", CLUSTERS_LAYER, onClusterLeave);
          if (readinessTimer !== null) window.clearTimeout(readinessTimer);
          if (!disposed) setMapStatus("ready");
        });
      } catch (error) {
        console.error("Unable to initialize permit map", error);
        if (!disposed) setMapStatus("error");
      }
    };

    observer = new ResizeObserver(() => {
      if (map) map.resize();
      else void initialize();
    });
    observer.observe(containerRef.current);
    void initialize();
    return () => {
      disposed = true;
      if (readinessTimer !== null) window.clearTimeout(readinessTimer);
      if (hoverCloseTimer !== null) window.clearTimeout(hoverCloseTimer);
      observer?.disconnect();
      hoverPopupRef.current?.remove();
      persistentPopupRef.current?.remove();
      map?.remove();
      mapRef.current = null;
    };
  }, [hasMappableProjects, mapAttempt]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded()) return;
    (map.getSource(SOURCE_ID) as GeoJSONSource | undefined)?.setData(toFeatureCollection(mappableProjects));
  }, [mappableProjects]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.getLayer(PROJECTS_LAYER)) return;
    map.setPaintProperty(PROJECTS_LAYER, "circle-radius", ["case", ["==", ["get", "id"], highlightedId], 19, 14]);
  }, [highlightedId]);

  useEffect(() => {
    const map = mapRef.current;
    const selected = mappableProjects.find((project) => project.id === selectedId);
    if (map && selected) map.panTo([selected.longitude, selected.latitude], { duration: 240 });
  }, [mappableProjects, selectedId]);

  if (!mappableProjects.length) return <div className="map-unavailable" role="status"><strong>Map location unavailable</strong><span>This permit does not include usable coordinates yet.</span></div>;
  return <div className="permit-map-frame" data-map-status={mapStatus}>
    <div ref={containerRef} className="permit-map maplibre-permit-map" role="region" aria-label="Map of Austin permit projects" aria-busy={mapStatus === "loading"} />
    {mapStatus === "loading" ? <div className="map-status-overlay" role="status">Loading map…</div> : null}
    {mapStatus === "error" ? <div className="map-status-overlay map-error" role="alert"><strong>Map temporarily unavailable</strong><span>OpenFreeMap did not finish loading.</span><button type="button" onClick={() => setMapAttempt((attempt) => attempt + 1)}>Try again</button></div> : null}
  </div>;
}
